import { useState, useMemo, SetStateAction } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GetFeedbacks } from '../../api/Courses/GetAllFeedbacks';
import { Link } from 'react-router-dom';
import Spinner from '../../components/Shared/Spinner';
import Input from '../../components/Shared/Input';

const Feedback = () => {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const itemsPerPage = 10; // Number of items per page

    const { data: FeedbackDTO, isLoading } = useQuery({
        queryFn: GetFeedbacks,
        queryKey: ['feedback'],
        staleTime: 0,
        refetchInterval: 0,
    });

    const feedback = FeedbackDTO?.data || [];

    const filteredFeedback = useMemo(() => {
        return feedback.filter(
            (f) =>
                f.student.firstName.toLowerCase().includes(search.toLowerCase()) ||
                f.student.lastName.toLowerCase().includes(search.toLowerCase()) || f.video.name.toLowerCase().includes(search.toLowerCase()) 
        );
    }, [feedback, search]);

    const paginatedFeedback = useMemo(() => {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredFeedback.slice(startIndex, endIndex);
    }, [filteredFeedback, page, itemsPerPage]);

    const totalPages = Math.ceil(filteredFeedback.length / itemsPerPage);

    const handleSearchChange = (e: { target: { value: SetStateAction<string>; }; }) => {
        setSearch(e.target.value);
        setPage(1); // Reset to the first page when searching
    };

    const handleNextPage = () => {
        setPage((prevPage) => Math.min(prevPage + 1, totalPages));
    };

    const handlePreviousPage = () => {
        setPage((prevPage) => Math.max(prevPage - 1, 1));
    };

    return (
        <div className="py-7">
            <h1 className="text-burntBlack font-bold text-[32px] mb-7">Student Feedbacks</h1>
            <div className="p-8 rounded-xl bg-white border-black/15 shadow">
                <Input
                    name='search'
                    type="text"
                    placeholder="Search by video or user name"
                    value={search}
                    onChange={handleSearchChange}
                    inputStyles='!h-[50px] mb-5 !w-80' 
                />
                <div className="grid grid-cols-3 gap-5">
                    {isLoading ? (
                        <div className="min-h-[50vh] flex justify-center items-center">
                            <Spinner />
                        </div>
                    ) : (
                        paginatedFeedback.map((feedback, index) => (
                            <div
                                className="col-span-1 border-black/15 border rounded-lg px-5 py-10 space-y-4"
                                key={index}
                            >
                                <h1 className="font-bold text-primary text-xl">
                                    {feedback.video.name}
                                </h1>
                                <Link to={`/dashboard/students/${feedback.studentId}`} className="font-medium">
                                    Issuer: {feedback.student.firstName} {feedback.student.lastName}
                                </Link>
                                <div>
                                    <p>Message:</p>
                                    <p>{feedback.message}</p>
                                </div>
                            </div>
                        ))
                    )}
                </div>
                <div className="mt-4 flex justify-between items-center">
                    <button
                        onClick={handlePreviousPage}
                        disabled={page === 1}
                        className={`p-2 border border-black/15 rounded ${page > 1 ? 'bg-blue-500 text-white' : ''}`}
                    >
                        Previous
                    </button>
                    <span>
                        Page {page} of {totalPages}
                    </span>
                    <button
                        onClick={handleNextPage}
                        disabled={page >= totalPages}
                        className={`p-2 border border-black/15 rounded ${
                            page < totalPages ? 'bg-blue-500 text-white' : ''
                        }`}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Feedback;
